import PropTypes from "prop-types";
import {cn} from "@/utils/cn";
import useSubscription from "@/hooks/useSubscription";
import {formatMoney} from "@/utils/formatMoney";
import {getCurrentRegion, isSubscriptionRegion} from "@/utils/regions";
import {observer} from "mobx-react";
import {useEffect, useMemo, useState} from "react";
import {product as productStore} from "@/store/Product";
import {price as priceStore} from "@/store/Price";
import {triggerEvent} from "@/utils/custom-events";

// pdp-v2 buy box (ported from the US theme). EU-specific adaptations:
//  - formatMoney comes from @/utils/formatMoney (EU has no @shopify/theme-currency)
//  - subscription UI is gated on the region (EU serves non-subscription markets too)
//  - UI copy is passed in via `labels` (German defaults below) instead of hardcoded English
//  - the Add to Cart button is local so EU's shared <AddToCart> stays untouched
//
// Two modes, chosen by ProductFormV2 (the default export):
//  - single product  -> renders <BuyBoxForm> directly (identical to the US buy box)
//  - `products` array -> renders <ProductToggle>: a "select product" card row that swaps
//    the buy box + the pre-rendered gallery/title/stars panels IN PLACE (no page reload)
//  - `products` + `soldOut` -> the card row ONLY: the page product is sold out, so Liquid renders
//    the back-in-stock card (pdp-v2-out-of-stock) in place of the tiers + Add to Cart
const DEFAULT_LABELS = {
  selectProduct: "Option auswählen",
  soldOut: "Ausverkauft",
  selectQuantity: "Menge auswählen",
  selectPurchaseType: "Wähle deine Kaufoption",
  // {n} is replaced with the delivery interval / unit count
  subscriptionTitle: "{n} Monatsvorrat",
  subBadge: "Spar-Abo",
  freeShipping: "Kostenloser Versand – für immer",
  pauseSkipCancel: "Jederzeit flexibel pausieren, überspringen oder kündigen",
  // {n} = delivery interval / unit count; {unit} = unit label (Beutel / Dosen …)
  supplyShips: "Alle {n} Monate automatisch {n} {unit} geliefert",
  oneTimePurchase: "Einmaliger Kauf",
  addToCart: "Jetzt kaufen",
  save: "Spare {n}%",
  //   (non-breaking space) keeps "75 €" together so the price never splits across a line break.
  shippingNote:
    "Für Abonnements und Einzelkäufe außerhalb Deutschlands fällt eine Versandgebühr an. Ab 75 € versandkostenfrei.",
};

const fmt = (tpl, n) => (tpl ?? "").replaceAll("{n}", n);

// The buy box for ONE product. Reads the singleton stores, which ProductToggle
// re-points to the active product BEFORE this component (re)mounts via its key.
const BuyBoxForm = observer(
  ({product, unitTypeLabel, showGift = true, giftLabel, giftValue, subTags, labels}) => {
    const L = {...DEFAULT_LABELS, ...(labels || {})};

    // unitTypeLabel may carry a "singular|plural" pair (e.g. "Dose|Dosen") for units whose noun
    // inflects. With no "|" it stays invariant (e.g. "Beutel"). Prices are per-unit (always
    // singular); the quantity tiles choose singular/plural by count.
    const [unitSingularRaw, unitPluralRaw] = (unitTypeLabel || "").split("|");
    const unitSingular = (unitSingularRaw || "").trim();
    const unitPlural = (unitPluralRaw || unitSingularRaw || "").trim();

    const {
      hasSubscription,
      isActive,
      sellingPlanId,
      setSellingPlanId,
      setSubscriptionActive,
    } = useSubscription(product, productStore.sellingPlanId);

    // EU serves ES/IT/UK where subscriptions are not offered — hide the subscribe
    // option there even if the product still carries selling plans.
    const regionSupportsSubscription = isSubscriptionRegion(getCurrentRegion());
    const showSubscription = hasSubscription && regionSupportsSubscription;

    const otpPrice = priceStore?.pricesQtd1?.otpPrice;

    // Subscription selling plans, ordered for the tier tiles. EU's helper-product-config
    // already filters selling_plan_groups by subscription_app_id, so (unlike the US theme)
    // we do NOT additionally match the group name on "subscribe" — that misses German
    // group names (e.g. "Abonnieren & Sparen"). Sort by discount % (rises with the tier:
    // 20 -> 1 unit, 25 -> 2, 30 -> 3), tie-breaking on delivery interval.
    const subscribeAndSavePlans = useMemo(() => {
      const plans = product.sellingPlanGroups?.flatMap(g => g.selling_plans) ?? [];
      const discountPct = p => {
        const v = parseFloat(p?.price_adjustments?.[0]?.value);
        return Number.isFinite(v) ? v : Number.MAX_SAFE_INTEGER;
      };
      const intervalMonths = p => {
        const match = `${p?.options?.[0]?.value ?? ""} ${p?.name ?? ""}`.match(/\d+/);
        return match ? parseInt(match[0], 10) : Number.MAX_SAFE_INTEGER;
      };
      return [...plans].sort(
        (a, b) => discountPct(a) - discountPct(b) || intervalMonths(a) - intervalMonths(b),
      );
    }, [product]);

    // pdp-v2: each subscription tier is its own selling plan (20/25/30% at qty 1/2/3), so the
    // subscription ROW is priced from the plan that matches the SELECTED quantity — the live
    // sellingPlanId while a subscription is active, and the quantity tile's matching tier while
    // one-time is active. This keeps the subscription price tracking the quantity in BOTH modes
    // (previously it fell back to the qty-1 plan and froze whenever one-time was selected).
    const displayedSellingPlanId =
      isActive && sellingPlanId
        ? sellingPlanId
        : subscribeAndSavePlans[(productStore.quantity || 1) - 1]?.id;
    const subscriptionPrice =
      priceStore?.pricesData?.find(
        p =>
          p.id === productStore.variantId &&
          !!displayedSellingPlanId &&
          p.selling_plan === displayedSellingPlanId,
      ) ?? priceStore?.pricesQtd1?.subscriptionPrice;

    // Buy-box tracking: mark this buy box's arm on mount (also pushes the buybox_view exposure).
    useEffect(() => {
      productStore.setArm("v2");
    }, []);

    // Publish the shown product's config so the (separate, non-remounted) sticky bar can reflect
    // the current product's title/image/price after an in-place product swap.
    useEffect(() => {
      productStore.setCurrentProduct(product);
    }, [product]);

    useEffect(() => {
      if (productStore.sellingPlanId !== sellingPlanId) {
        productStore.setSellingPlan(sellingPlanId);
      }
    }, [sellingPlanId]);

    useEffect(() => {
      const params = new URLSearchParams(location.search);
      const forceOtp = params.get("box") === "otp";
      if (forceOtp || !showSubscription) setSubscriptionActive(false);
      const qtd = Number(params.get("quantity"));
      // Tier to preselect (1-based): an explicit ?quantity wins; otherwise default to the
      // RECOMMENDED tile (falls back to the 2nd tier) so every page opens on a deliberate tier.
      let tierIndex = Number.isFinite(qtd) && qtd > 0 ? qtd : null;
      if (forceOtp || !showSubscription) {
        if (tierIndex) productStore.setQuantity(tierIndex);
        return;
      }
      if (tierIndex === null) {
        const rec = subTags?.find(
          t => (t.tag || "").toString().trim().toUpperCase() === "RECOMMENDED",
        );
        tierIndex = rec?.index || 2;
      }
      const plan = subscribeAndSavePlans[tierIndex - 1];
      if (plan) {
        setSellingPlanId(plan.id);
        productStore.setSellingPlan(plan.id);
      }
    }, [setSubscriptionActive]);

    const onAddToCart = () => {
      triggerEvent("cart:added", {
        productId: product.id,
        productName: product.title,
        variantId: productStore.variantId,
        quantity: productStore.quantity,
        frequencyId: productStore.sellingPlanId,
        subtotal: product.price,
      });
    };

    const handleAddToCart = async e => {
      e.preventDefault();
      await productStore.addToCart();
      onAddToCart();
    };

    const selectedPlanIndex = subscribeAndSavePlans.findIndex(p => p.id === sellingPlanId);
    // Delivery window = unit count: 1 -> every 1 month, 2 -> 2 months, 3 -> 3 months.
    const selectedPlanMonths =
      isActive && selectedPlanIndex >= 0
        ? selectedPlanIndex + 1
        : Math.max(1, productStore.quantity || 1);

    // Keep quantity aligned with the selected subscription tier (unit count = plan index + 1)
    // so the OTP price, add-to-cart subtotal and cart quantity match the preselected tier on
    // first load — not only after the user clicks a tile.
    useEffect(() => {
      if (isActive && selectedPlanIndex >= 0 && productStore.quantity !== selectedPlanIndex + 1) {
        productStore.setQuantity(selectedPlanIndex + 1);
      }
    }, [isActive, selectedPlanIndex]);

    // Buy-box tracking: carry the selected delivery interval (months) for subscriptions, else null.
    useEffect(() => {
      productStore.setFrequency(isActive ? selectedPlanMonths : null);
    }, [isActive, selectedPlanMonths]);

    // Buy-button subtotal. OTP price entries are quantity-tiered line totals, so they
    // already reflect quantity; the subscription price is the per-unit (qty 1) value,
    // so multiply it by the selected quantity to get the subscription subtotal.
    const qty = productStore.quantity || 1;
    const buyNowPrice = isActive
      ? (subscriptionPrice?.price ?? 0) * qty
      : otpPrice?.price;

    return (
      <div className="flex flex-col gap-6">
        <input
          className="sr-only"
          type="radio"
          name="id"
          value={productStore.variantId}
          readOnly
          aria-label="Variant ID"
        />

        {/* Quantity selector */}
        {subscribeAndSavePlans.length > 0 && (
          <div className="flex flex-col gap-6">
            <p className="m-0 text-[14px] font-semibold text-[#0d2e25]">{L.selectQuantity}</p>
            <div className="grid grid-cols-3 gap-[10px]">
              {subscribeAndSavePlans.map((plan, i) => {
                const tag = subTags?.find(({index}) => i + 1 === index)?.tag ?? "";
                const bottleCount = i + 1;
                const isSelected = isActive
                  ? sellingPlanId === plan.id
                  : productStore.quantity === bottleCount;
                const savePercent = Math.round(plan.price_adjustments?.[0]?.value ?? 0);
                return (
                  <button
                    key={plan.id}
                    type="button"
                    // Stable hook for analytics (GTM reads the selected quantity via
                    // button[class*="-qty-"][aria-pressed="true"]) + a11y.
                    aria-pressed={isSelected}
                    className={cn(
                      `purchase-${isActive ? "sub" : "otp"}-qty-${bottleCount}`,
                      "relative flex flex-col items-center justify-center gap-1 text-center rounded-[20px] pt-4 pb-3 px-4 cursor-pointer transition-all border",
                      isSelected
                        ? "border-2 border-[#a0c482] bg-white/40 shadow-[0px_4px_22.8px_0px_rgba(15,66,52,0.1)]"
                        : "border-[rgba(13,46,37,0.2)]"
                    )}
                    onClick={() => {
                      productStore.setQuantity(bottleCount);
                      if (isActive) {
                        setSellingPlanId(plan.id);
                        productStore.setSellingPlan(plan.id);
                      }
                    }}
                  >
                    {tag && (
                      <span
                        className={cn(
                          "absolute -top-[11px] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-[11px] leading-[11px] font-semibold text-[#0d2e25]",
                          i === 2 ? "bg-[#f0b136]" : "bg-[#a0c482]"
                        )}
                      >
                        {tag}
                      </span>
                    )}
                    <span className="text-[16px] leading-[20px] font-medium text-[#0f4234]">
                      {bottleCount} {bottleCount === 1 ? unitSingular : unitPlural}
                    </span>
                    {savePercent > 0 && (
                      <span className="text-[12px] leading-[20px] font-bold text-[#8b9399]">{fmt(L.save, savePercent)}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Purchase type selector + Add to Cart */}
        <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <p className="m-0 text-[14px] font-semibold text-[#0d2e25]">{L.selectPurchaseType}</p>

          {/* Subscription option */}
          {showSubscription && (
            <label
              className={cn(
                "purchase-type-sub",
                "relative flex flex-col gap-2 rounded-[20px] p-4 cursor-pointer transition-all border",
                isActive ? "bg-white border-2 border-[#a0c482]" : "bg-[#f7f5f1] border-[#b8b9ba]"
              )}
            >
              <input
                type="radio"
                className="sr-only"
                name={`${product.handle}-purchase-type`}
                checked={isActive}
                onChange={() => {
                  setSubscriptionActive(true);
                  const q = productStore.quantity || 1;
                  const plan = subscribeAndSavePlans[q - 1] ?? subscribeAndSavePlans[0];
                  if (plan) {
                    setSellingPlanId(plan.id);
                    productStore.setSellingPlan(plan.id);
                  }
                }}
                aria-label="Abo & Sparen"
              />
              {L.subBadge && (
                <span className="absolute -top-[11px] left-4 z-10 inline-flex items-center rounded-full bg-[#a0c482] px-3 py-1 text-[11px] font-semibold uppercase leading-none text-[#0d2e25] whitespace-nowrap">
                  {L.subBadge}
                </span>
              )}
              <div className="flex items-start gap-3 max-[400px]:gap-2 lg:gap-[18px] w-full">
                <span
                  className={cn(
                    "relative grid place-items-center shrink-0 w-5 h-5 rounded-[10px] border bg-white",
                    isActive ? "border-[#0d2e25]" : "border-[#8b9399]"
                  )}
                >
                  {isActive && <span className="w-3 h-3 rounded-full bg-[#0d2e25]" />}
                </span>
                <span className="flex-1 text-[20px] max-[440px]:text-[18px] max-[420px]:text-[16px] max-[400px]:text-[15px] max-[360px]:text-[14px] leading-[20px] font-normal text-[#0d2e25] whitespace-nowrap">
                  {fmt(L.subscriptionTitle, selectedPlanMonths)}
                </span>
                <div className="flex flex-wrap justify-end items-baseline gap-x-1.5 text-right min-w-0">
                  {subscriptionPrice?.compareAtPrice && subscriptionPrice.compareAtPrice > subscriptionPrice.price && (
                    <span className="text-[16px] max-[400px]:text-[14px] lg:text-[18px] font-normal line-through text-[#727272]">{formatMoney(subscriptionPrice.compareAtPrice)}</span>
                  )}
                  <span className="text-[18px] max-[400px]:text-[16px] lg:text-[20px] font-semibold text-[#0d2e25]">{subscriptionPrice?.price ? formatMoney(subscriptionPrice.price) : "—"}</span>
                  <span className="w-full text-right text-[14px] font-light text-[#727272]">/{unitSingular}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 w-full">
                  <ul className="flex flex-col gap-2 list-none m-0 p-0">
                    {[
                      L.freeShipping,
                      L.pauseSkipCancel,
                      fmt(L.supplyShips, selectedPlanMonths).replaceAll("{unit}", selectedPlanMonths === 1 ? unitSingular : unitPlural),
                    ].map(benefit => (
                      <li key={benefit} className="flex items-center gap-[18px] text-[14px] font-semibold text-[#727272]">
                        <svg className="shrink-0 w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M3 8.5l3 3 7-7" stroke="#a0c482" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  {showGift && giftLabel && (
                    <div className="flex items-center gap-[10px] rounded-[16px] bg-[#0f4234] p-4 w-full">
                      <svg className="shrink-0 text-white w-5 h-5" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
                      <span className="text-[14px] font-bold text-white uppercase">{giftLabel}</span>
                      {giftValue && <span className="ml-auto text-[14px] font-semibold text-white whitespace-nowrap">({giftValue})</span>}
                    </div>
                  )}
                </div>
            </label>
          )}

          {/* One-time purchase option */}
          <label
            className={cn(
              "purchase-type-otp",
              "flex flex-col gap-2 rounded-[20px] bg-white p-4 cursor-pointer transition-all overflow-hidden border",
              !isActive ? "bg-white border-2 border-[#a0c482]" : "bg-[#f7f5f1] border-[#b8b9ba]"
            )}
          >
            <input
              type="radio"
              className="sr-only"
              name={`${product.handle}-purchase-type`}
              checked={!isActive}
              onChange={() => setSubscriptionActive(false)}
              aria-label="Einmaliger Kauf"
            />
            <div className="flex items-start gap-[18px] max-[400px]:gap-2 w-full">
              <span
                className={cn(
                  "relative grid place-items-center shrink-0 w-5 h-5 rounded-[10px] border bg-white",
                  !isActive ? "border-[#0d2e25]" : "border-[#8b9399]"
                )}
              >
                {!isActive && <span className="w-3 h-3 rounded-full bg-[#0d2e25]" />}
              </span>
              <span className="flex-1 text-[20px] max-[440px]:text-[18px] max-[420px]:text-[16px] max-[400px]:text-[15px] max-[360px]:text-[14px] leading-[20px] font-normal text-[#0d2e25] whitespace-nowrap">{L.oneTimePurchase}</span>
              <div className="flex flex-wrap justify-end items-baseline gap-x-1.5 text-right min-w-0">
                {otpPrice?.compareAtPrice && otpPrice.compareAtPrice > otpPrice.price && (
                  <span className="text-[16px] max-[400px]:text-[14px] lg:text-[18px] font-normal line-through text-[#727272]">{formatMoney(otpPrice.compareAtPrice / (otpPrice.quantity || 1))}</span>
                )}
                <span className="text-[18px] max-[400px]:text-[16px] lg:text-[20px] font-semibold text-[#0d2e25]">{otpPrice?.price ? formatMoney(otpPrice.price / (otpPrice.quantity || 1)) : "—"}</span>
                <span className="w-full text-right text-[14px] font-light text-[#727272]">/{unitSingular}</span>
              </div>
            </div>
          </label>
        </div>

        {/* Add to Cart button — local button (not the shared <AddToCart>) so we can apply the
            pill styling without touching that component. */}
        <div data-atc-cta>
          <button
            type="button"
            onClick={handleAddToCart}
            className="purchase-button-atc flex items-center justify-center w-full h-[55px] px-[25px] rounded-[56px] bg-[#f0b136] text-[#0d2e25] font-medium text-[18px] lg:text-[20px] border-0 transition-opacity hover:opacity-80"
          >
            <span className="flex items-center justify-center gap-[16px]">
              <span>{L.addToCart}</span>
              {buyNowPrice ? (
                <>
                  <span className="inline-block shrink-0 w-[7px] h-[7px] rounded-full bg-current" aria-hidden="true"></span>
                  <span>{formatMoney(buyNowPrice)}</span>
                </>
              ) : null}
            </span>
          </button>
        </div>
        {L.shippingNote && (
          <p className="mt-3 text-center text-[13px] leading-[18px] font-semibold text-[#727272]">{L.shippingNote}</p>
        )}
        </div>
      </div>
    );
  }
);

BuyBoxForm.propTypes = {
  product: PropTypes.object.isRequired,
  unitTypeLabel: PropTypes.string,
  showGift: PropTypes.bool,
  giftLabel: PropTypes.string,
  giftValue: PropTypes.string,
  subTags: PropTypes.array,
  labels: PropTypes.object,
};

// The "Option auswählen" product selector cards. A sold-out product (`available: false`) gets
// the Figma 2534:9510 card — dashed border, faded image + name, grey AUSVERKAUFT pill — whether or
// not it is the selected one; it stays clickable so shoppers can reach its back-in-stock signup.
const ProductToggleCards = ({products, activeIndex, onSelect, label, soldOutLabel}) => (
  <div className="flex flex-col gap-4">
    <p className="m-0 text-[14px] font-semibold text-[#0d2e25]">{label}</p>
    <div className="grid grid-cols-3 gap-4">
      {products.map((p, i) => {
        const isSelected = i === activeIndex;
        const isSoldOut = p.available === false;
        return (
          <button
            key={p.handle}
            type="button"
            aria-pressed={isSelected}
            className={cn(
              `purchase-toggle-${i + 1}`,
              "relative flex flex-col items-center justify-center rounded-[20px] pt-4 pb-3 px-2 cursor-pointer transition-all text-center",
              isSoldOut
                ? "gap-1 border-2 border-dashed border-[rgba(13,46,37,0.2)] bg-[#faf9f7] shadow-[0px_4px_22.8px_0px_rgba(15,66,52,0.1)]"
                : isSelected
                  ? "gap-2 border-2 border-[#a0c482] bg-[#faf9f7] shadow-[0px_4px_22.8px_0px_rgba(15,66,52,0.1)]"
                  : "gap-2 border border-[rgba(13,46,37,0.2)] bg-transparent hover:border-[rgba(13,46,37,0.4)]"
            )}
            onClick={() => onSelect(i)}
          >
            {isSoldOut && (
              <span className="absolute top-3 left-1/2 -translate-x-1/2 max-w-[calc(100%-8px)] rounded-full bg-[#757575] px-[10px] max-[400px]:px-[6px] py-1 text-[11px] max-[400px]:text-[9px] leading-[12px] font-bold uppercase text-white whitespace-nowrap">
                {soldOutLabel}
              </span>
            )}
            {p.cardImage && (
              <img
                src={p.cardImage}
                alt={p.label}
                loading="lazy"
                className={cn(
                  "h-[87px] w-[69px] object-contain drop-shadow-[8px_14px_16px_rgba(35,50,50,0.22)]",
                  isSoldOut && "opacity-30"
                )}
              />
            )}
            <span className={cn("text-[14px] leading-[20px] font-medium text-[#0d2e25]", isSoldOut && "opacity-30")}>
              {p.label}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

ProductToggleCards.propTypes = {
  products: PropTypes.array.isRequired,
  activeIndex: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  label: PropTypes.string,
  soldOutLabel: PropTypes.string,
};

// Multi-product buy box. Renders the "Option auswählen" card row + the active (page) product's
// <BuyBoxForm>. Switching products triggers a FULL-PAGE in-place swap (window.__cgSwapProduct):
// the entrypoint fetches the selected product's own page and replaces this whole section, so only
// ONE product is ever server-rendered (no hidden panels for the others). repointStores() just keeps
// the shared stores aligned with the page product on mount so the ATC variant id is always correct.
const ProductToggle = ({
  products,
  activeIndex = 0,
  soldOut = false,
  subTags,
  showGift,
  giftLabel,
  giftValue,
  labels,
}) => {
  const L = {...DEFAULT_LABELS, ...(labels || {})};
  const safeInitial = Math.min(Math.max(activeIndex, 0), products.length - 1);
  const [idx] = useState(safeInitial);
  const active = products[idx];

  const repointStores = i => {
    const p = products[i];
    if (!p) return;
    if (p.variantId != null) productStore.setVariantId(p.variantId);
    if (p.prices) priceStore.setPricesData(p.prices);
  };

  // Mount: keep the shared stores aligned with the page product. (The section's store JSON
  // already holds it, but re-point defensively so the ATC variant id is always correct.)
  useEffect(() => {
    repointStores(safeInitial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelect = i => {
    if (i === idx || i < 0) return;
    const p = products[i];
    if (!p?.url) return;
    // In the theme preview each product is behind ?view=<template-suffix>, so target that
    // product's own view; in production (templates assigned) use the clean URL.
    const inPreview = new URLSearchParams(window.location.search).has("view");
    const url = inPreview && p.view ? `${p.url}?view=${p.view}` : p.url;
    // Full-page in-place swap: the product-v2 entrypoint fetches the page and replaces this
    // whole section (URL + Back/Forward handled there). Falls back to a normal navigation.
    if (typeof window.__cgSwapProduct === "function") {
      window.__cgSwapProduct(url);
    } else {
      window.location.href = url;
    }
  };

  const cards = (
    <ProductToggleCards
      products={products}
      activeIndex={idx}
      onSelect={onSelect}
      label={L.selectProduct}
      soldOutLabel={L.soldOut}
    />
  );

  // Page product sold out: cards only — the back-in-stock card is server-rendered below.
  if (soldOut) return cards;

  return (
    <div className="flex flex-col gap-6">
      {cards}
      <BuyBoxForm
        key={active.handle}
        product={active.config}
        unitTypeLabel={active.unitTypeLabel}
        subTags={subTags}
        showGift={showGift}
        giftLabel={giftLabel}
        giftValue={giftValue}
        labels={labels}
      />
    </div>
  );
};

ProductToggle.propTypes = {
  products: PropTypes.array.isRequired,
  activeIndex: PropTypes.number,
  soldOut: PropTypes.bool,
  subTags: PropTypes.array,
  showGift: PropTypes.bool,
  giftLabel: PropTypes.string,
  giftValue: PropTypes.string,
  labels: PropTypes.object,
};

// Chooser (no hooks): multi-product toggle when `products` has >1 entry, else the
// plain single-product buy box (identical to the US theme).
const ProductFormV2 = props => {
  if (Array.isArray(props.products) && props.products.length > 1) {
    return <ProductToggle {...props} />;
  }
  return <BuyBoxForm {...props} />;
};

ProductFormV2.propTypes = {
  // single-product mode
  product: PropTypes.object,
  unitTypeLabel: PropTypes.string,
  showGift: PropTypes.bool,
  giftLabel: PropTypes.string,
  giftValue: PropTypes.string,
  subTags: PropTypes.array,
  labels: PropTypes.object,
  // multi-product (toggle) mode
  products: PropTypes.array,
  activeIndex: PropTypes.number,
  soldOut: PropTypes.bool,
};

export default ProductFormV2;
